import { useEffect } from "react";

/**
 * Lightweight client-side SEO hook. Sets <title> + meta tags for the current
 * route. Cleans up by restoring the prior tag values when the component
 * unmounts so back/forward navigation produces correct meta.
 *
 * NOTE: Because this is a SPA, the initial HTML still wins for crawlers that
 * don't render JS (older bots). Modern Googlebot, Bingbot, and most social
 * scrapers render JS, so per-route titles still show up correctly in SERPs.
 *
 * For perfect crawler support, you'd need SSR/prerendering. For a soft launch
 * this is the right tradeoff (zero infra change).
 */
const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return { el, created };
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return { el, created };
};

export default function useSEO({ title, description, canonicalPath, ogImage }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const tracked = [];
    if (description) {
      tracked.push(upsertMeta('meta[name="description"]', { name: "description", content: description }));
      tracked.push(upsertMeta('meta[property="og:description"]', { property: "og:description", content: description }));
      tracked.push(upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description }));
    }
    if (title) {
      tracked.push(upsertMeta('meta[property="og:title"]', { property: "og:title", content: title }));
      tracked.push(upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title }));
    }
    if (canonicalPath) {
      const fullUrl = `https://mycentennialplan.com${canonicalPath}`;
      tracked.push(upsertCanonical(fullUrl));
      tracked.push(upsertMeta('meta[property="og:url"]', { property: "og:url", content: fullUrl }));
    }
    if (ogImage) {
      tracked.push(upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage }));
      tracked.push(upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage }));
    }

    return () => {
      document.title = prevTitle;
      // Don't remove originals — only restore when we created new ones
      tracked.forEach(({ el, created }) => {
        if (created && el && el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [title, description, canonicalPath, ogImage]);
}
